/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { format, isEqual, isToday, isBefore, startOfDay } from "date-fns";
import Button from "../Button";
import Icon from "../Icon";
import { getDaysInCalendar, localDate, nextMonth, previousMonth } from "../../../utils/helpers/date";
import { DAYS_OF_WEEK_MON_FIRST, DAYS_OF_WEEK_SUN_FIRST } from "../../../constants/constants";
import { useConfig } from "../../../lib/react-query/useConfig";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { useClickableA11y } from "../../../hooks/a11y/useClickableA11y";
import { useTranslate } from "../../../hooks/useTranslate";
import Text from "../Text";
import { focusRingInsetStyles } from "../../GlobalWidgetStyle";
import FormatDateTime from "../../formatters/FormatDateTime";

const CalendarWrapper = styled.div`
  display: ${({ $show }) => ($show ? "block" : "none")};
  position: absolute;
  ${({ $position }) => $position === "bottom" && `top: calc(100% + 0.5rem);`}
  ${({ $position }) => $position === "top" && `bottom: calc(100% + 0.5rem);`}
  ${({ $align }) => $align === "right" && `right: 0;`}
  ${({ $align }) => $align === "left" && `left: 0;`}
  background: rgba(${({ theme }) => theme.colors.surface.rgba});
  border-radius: 0.5rem;
  padding: 1rem;
  z-index: 1000;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px 0px rgba(0, 0, 0, 0.07);
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CalendarMain = styled.div`
  display: grid;
  gap: 0.125rem;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 2rem);
  gap: 0.125rem;
`;

const CalendarWeekDay = styled((props) => <Text {...props} />)`
  text-align: center;
  text-transform: uppercase;
  color: rgba(${({ theme }) => theme.colors.textSecondary.rgba});
  opacity: 0.7;
`;

const CalendarDay = styled((props) => <Text {...props} />)`
  align-content: center;
  justify-items: center;
  aspect-ratio: 1;
  text-align: center;
  border-radius: 0.25rem;
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  transition: background-color 0.2s ease, color 0.2s ease;
  cursor: pointer;

  &:focus-visible {
    ${focusRingInsetStyles}
  }

  @media (hover: hover) {
    &:hover {
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
      background: rgba(${({ theme }) => theme.colors.accent.rgba});
    }
  }

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      background: rgba(${theme.colors.accent.rgba});
      color: rgba(${theme.colors.accentContrast.rgba});
    `}

  ${({ $isToday, $isSelected }) =>
    $isToday && !$isSelected &&
    css`
      font-weight: bold;
      background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.1);
    `}

  ${({ $isOutsideMonth }) =>
    $isOutsideMonth &&
    css`
      opacity: 0.5;
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.3;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const CalendarDayCell = ({ date, isSelected, isToday: isDayToday, isOutsideMonth, isDisabled, onChange }) => {
  const handleClick = () => onChange(date);
  const a11yProps = useClickableA11y({ onClick: handleClick, disabled: isDisabled, selected: isSelected });

  return (
    <CalendarDay
      color="dark"
      onClick={isDisabled ? undefined : handleClick}
      $isSelected={isSelected}
      $isToday={isDayToday}
      $isOutsideMonth={isOutsideMonth}
      $isDisabled={isDisabled}
      aria-label={format(date, "EEEE, MMMM d, yyyy")}
      aria-current={isDayToday ? "date" : undefined}
      {...a11yProps}
    >
      {format(date, "d")}
    </CalendarDay>
  );
};

const CalendarDatePicker = ({ selectedDate, show, position = "bottom", align = "right", minDate, onChange, triggerRef, onClose }) => {
  const minDay = minDate ? startOfDay(minDate) : null;
  const calendarRef = useRef(null);
  const { _t } = useTranslate();
  const { data: config } = useConfig();
  const mondayIsFirstDay = config?.monday_is_first_day;
  const weekStartsOn = mondayIsFirstDay ? 1 : 0;
  const DAYS_OF_WEEK = mondayIsFirstDay ? DAYS_OF_WEEK_MON_FIRST : DAYS_OF_WEEK_SUN_FIRST;
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? localDate(selectedDate) : new Date());
  const daysInCalendar = useMemo(() => getDaysInCalendar(currentMonth, weekStartsOn), [currentMonth, weekStartsOn]);

  useClickOutside(() => {
    if (show) {
      onClose();
    }
  }, [triggerRef.current, calendarRef.current]);

  const handlePrevMonth = () => {
    setCurrentMonth(previousMonth(currentMonth));
  };

  const handleNextMonth = () => {
    setCurrentMonth(nextMonth(currentMonth));
  };

  return (
    <CalendarWrapper ref={calendarRef} $show={show} $position={position} $align={align}>
      <CalendarHeader>
        <Button
          type="button"
          size="icon"
          variant="transparent"
          onClick={handlePrevMonth}
          icon={<Icon.ChevronLeft />}
          aria-label={_t("Previous month")}
        />
        <Text color="dark" weight="bold">
          <FormatDateTime value={currentMonth} format={FormatDateTime.FORMAT.MONTH} />{" "}
          <FormatDateTime value={currentMonth} format={FormatDateTime.FORMAT.YEAR} />
        </Text>
        <Button
          type="button"
          size="icon"
          variant="transparent"
          onClick={handleNextMonth}
          icon={<Icon.ChevronRight />}
          aria-label={_t("Next month")}
        />
      </CalendarHeader>

      <CalendarMain>
        <CalendarGrid>
          {DAYS_OF_WEEK.map((day) => (
            <CalendarWeekDay size="tiny" key={day}>
              {day}
            </CalendarWeekDay>
          ))}
        </CalendarGrid>

        <CalendarGrid>
          {daysInCalendar.map(({ date, isOutsideMonth }, index) => {
            const isDisabled = minDay ? isBefore(startOfDay(date), minDay) : false;
            const isSelected = !!selectedDate && isEqual(localDate(selectedDate), date);
            const isDayToday = isToday(date);
            return (
              <CalendarDayCell
                key={index}
                date={date}
                isSelected={isSelected}
                isToday={isDayToday}
                isOutsideMonth={isOutsideMonth}
                isDisabled={isDisabled}
                onChange={onChange}
              />
            );
          })}
        </CalendarGrid>
      </CalendarMain>
    </CalendarWrapper>
  );
};

export default CalendarDatePicker;
